import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import FixedActions from '../components/FixedActions';
import ProjectDetailsModal from '../components/ProjectDetailsModal';
import { projectData, serviceCategories } from '../data/projectData';
import { ArrowLeft, Eye, MapPin, Calendar, Ruler } from 'lucide-react';
import { trackServiceView, trackProjectView } from '../utils/analytics';

const ServicePage = () => {
  const { service } = useParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(0);
  const [selectedProject, setSelectedProject] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Determine category from current path
  const currentPath = window.location.pathname;
  const category = currentPath.includes('/sports/') ? 'sports' : 'construction';

  // Get service data
  const serviceKey = service;
  const projects = projectData[serviceKey] || [];
  const serviceName = serviceCategories[category]?.[serviceKey] || 'Service';

  useEffect(() => {
    // Reset active tab when service changes
    setActiveTab(0);
    
    // Track service page view
    if (serviceName && serviceName !== 'Service') {
      trackServiceView(serviceName);
    }
  }, [serviceKey, serviceName]);

  // If service not found, redirect to category page
  useEffect(() => {
    if (!projects.length) {
      navigate(`/${category}`);
    }
  }, [projects.length, category, navigate]);

  const openModal = (project) => {
    setSelectedProject(project);
    setIsModalOpen(true);
    
    // Track project view
    if (project && project.title) {
      trackProjectView(project.title);
    }
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedProject(null);
  };

  const currentProject = projects[activeTab];

  if (!projects.length) {
    return null; // Will redirect in useEffect
  }

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg-primary)', overflowX: 'hidden', width: '100%' }}>
      <Header />
      <FixedActions />
      
      {/* Hero Section */}
      <section style={{ 
        padding: '120px 0 40px',
        background: 'linear-gradient(135deg, #004534 0%, #006B4A 100%)',
        position: 'relative',
        overflow: 'hidden'
      }}>
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: currentProject ? `url("${currentProject.images[0]}") center/cover` : '',
          opacity: 0.1
        }} />
        
        <div className="container" style={{ position: 'relative', zIndex: 2 }}>
          <button
            onClick={() => navigate(`/${category}`)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              background: 'rgba(255, 255, 255, 0.2)',
              color: 'white',
              border: 'none',
              padding: '10px 20px',
              borderRadius: '25px',
              cursor: 'pointer',
              marginBottom: '30px',
              transition: 'all 0.3s ease'
            }}
            onMouseEnter={(e) => {
              e.target.style.background = 'rgba(255, 255, 255, 0.3)';
            }}
            onMouseLeave={(e) => {
              e.target.style.background = 'rgba(255, 255, 255, 0.2)';
            }}
          >
            <ArrowLeft size={18} /> Back to {category.charAt(0).toUpperCase() + category.slice(1)}
          </button>

          <div style={{ 
            textAlign: 'center', 
            maxWidth: '800px', 
            margin: '0 auto',
            color: 'white'
          }}>
            <h1 style={{ 
              fontSize: '3rem', 
              fontWeight: 700, 
              marginBottom: '16px',
              lineHeight: 1.2
            }}>
              {serviceName}
            </h1>
            <p style={{ 
              fontSize: '1.125rem', 
              marginBottom: '40px',
              opacity: 0.9,
              lineHeight: 1.6
            }}>
              Explore our portfolio of {serviceName.toLowerCase()} projects showcasing quality craftsmanship and attention to detail.
            </p>
          </div>
        </div>
      </section>

      {/* Project Tabs */}
      <section style={{ padding: '30px 0 0', background: 'white' }}>
        <div className="container">
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            marginBottom: '20px',
            borderBottom: '2px solid #f0f0f0',
            overflowX: 'auto',
            WebkitOverflowScrolling: 'touch',
            scrollbarWidth: 'none',
            msOverflowStyle: 'none'
          }} className="project-tabs">
            {projects.map((project, index) => (
              <button
                key={index}
                onClick={() => setActiveTab(index)}
                style={{
                  padding: '15px 25px',
                  border: 'none',
                  background: 'none',
                  fontSize: '1rem',
                  fontWeight: 600,
                  color: activeTab === index ? 'var(--brand-dark)' : 'var(--text-secondary)',
                  cursor: 'pointer',
                  borderBottom: activeTab === index ? '3px solid var(--brand-primary)' : '3px solid transparent',
                  transition: 'all 0.3s ease',
                  whiteSpace: 'nowrap',
                  flexShrink: 0
                }}
                onMouseEnter={(e) => {
                  if (activeTab !== index) {
                    e.target.style.color = 'var(--brand-dark)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (activeTab !== index) {
                    e.target.style.color = 'var(--text-secondary)';
                  }
                }}
              >
                Project {index + 1}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Project Content */}
      <section style={{ padding: '20px 0 80px', background: 'white' }}>
        <div className="container">
          {currentProject && (
            <div style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: '60px',
              alignItems: 'center'
            }}>
              {/* Project Images */}
              <div style={{ position: 'relative' }}>
                <div style={{
                  width: '100%',
                  aspectRatio: '3/2',
                  maxHeight: '400px',
                  borderRadius: '20px',
                  overflow: 'hidden',
                  position: 'relative',
                  boxShadow: '0 20px 40px rgba(0,0,0,0.1)'
                }}>
                  <img
                    src={currentProject.images[0]}
                    alt={currentProject.title}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover'
                    }}
                  />
                  
                  {/* View Details Overlay */}
                  <div style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: 'linear-gradient(to bottom, transparent 60%, rgba(0,0,0,0.7))',
                    display: 'flex',
                    alignItems: 'flex-end',
                    justifyContent: 'center',
                    padding: '30px'
                  }}>
                    <button
                      onClick={() => openModal(currentProject)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        background: 'var(--brand-primary)',
                        color: 'var(--brand-dark)',
                        border: 'none',
                        padding: '12px 24px',
                        borderRadius: '25px',
                        fontSize: '1rem',
                        fontWeight: 600,
                        cursor: 'pointer',
                        transition: 'all 0.3s ease'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.transform = 'scale(1.05)';
                        e.target.style.boxShadow = '0 10px 30px rgba(211, 255, 98, 0.4)';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.transform = 'scale(1)';
                        e.target.style.boxShadow = 'none';
                      }}
                    >
                      <Eye size={18} /> View Details
                    </button>
                  </div>
                </div>

                {/* Additional Images Preview */}
                {currentProject.images.length > 1 && (
                  <div style={{
                    display: 'flex',
                    gap: '10px',
                    marginTop: '20px'
                  }}>
                    {currentProject.images.slice(1, 4).map((image, index) => (
                      <div
                        key={index}
                        style={{
                          flex: 1,
                          height: '80px',
                          borderRadius: '10px',
                          overflow: 'hidden',
                          cursor: 'pointer'
                        }}
                        onClick={() => openModal(currentProject)}
                      >
                        <img
                          src={image}
                          alt={`${currentProject.title} - ${index + 2}`}
                          style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                            transition: 'transform 0.3s ease'
                          }}
                          onMouseEnter={(e) => {
                            e.target.style.transform = 'scale(1.1)';
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.transform = 'scale(1)';
                          }}
                        />
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Project Info */}
              <div>
                <h2 style={{
                  fontSize: '2.25rem',
                  fontWeight: 700,
                  color: 'var(--brand-dark)',
                  marginBottom: '20px',
                  lineHeight: 1.2
                }}>
                  {currentProject.title}
                </h2>

                <p style={{
                  fontSize: '1.125rem',
                  color: 'var(--text-secondary)',
                  lineHeight: 1.6,
                  marginBottom: '30px'
                }}>
                  {currentProject.description}
                </p>

                {/* Quick Info */}
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  gap: '20px',
                  marginBottom: '30px'
                }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    padding: '15px',
                    background: '#FAFFEE',
                    borderRadius: '12px'
                  }}>
                    <Ruler size={20} color="var(--brand-dark)" />
                    <div>
                      <div style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>Built Area</div>
                      <div style={{ fontWeight: 600, color: 'var(--brand-dark)' }}>{currentProject.builtArea}</div>
                    </div>
                  </div>

                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    padding: '15px',
                    background: '#FAFFEE',
                    borderRadius: '12px'
                  }}>
                    <Calendar size={20} color="var(--brand-dark)" />
                    <div>
                      <div style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>Completed</div>
                      <div style={{ fontWeight: 600, color: 'var(--brand-dark)' }}>{currentProject.completionDate}</div>
                    </div>
                  </div>

                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    padding: '15px',
                    background: '#FAFFEE',
                    borderRadius: '12px',
                    gridColumn: 'span 2'
                  }}>
                    <MapPin size={20} color="var(--brand-dark)" />
                    <div>
                      <div style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>Location</div>
                      <div style={{ fontWeight: 600, color: 'var(--brand-dark)' }}>{currentProject.location}</div>
                    </div>
                  </div>
                </div>

                {/* Materials */}
                <div>
                  <h3 style={{
                    fontSize: '1.125rem',
                    fontWeight: 600,
                    color: 'var(--brand-dark)',
                    marginBottom: '12px'
                  }}>
                    Materials Used
                  </h3>
                  <div style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: '8px'
                  }}>
                    {currentProject.materials.map((material, index) => (
                      <span
                        key={index}
                        style={{
                          background: 'white',
                          color: 'var(--brand-dark)',
                          padding: '6px 14px',
                          borderRadius: '15px',
                          fontSize: '0.875rem',
                          fontWeight: 500,
                          border: '1px solid rgba(211, 255, 98, 0.4)'
                        }}
                      >
                        {material}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      <Footer />

      {/* Project Details Modal */}
      <ProjectDetailsModal
        project={selectedProject}
        isOpen={isModalOpen}
        onClose={closeModal}
      />

      <style jsx>{`
        .project-tabs::-webkit-scrollbar {
          display: none;
        }
        
        @media (max-width: 1024px) {
          .container {
            padding: 0 20px !important;
            max-width: 100%;
            overflow-x: hidden;
          }
          
          div[style*="gridTemplateColumns: '1fr 1fr'"] {
            grid-template-columns: 1fr !important;
            gap: 30px !important;
          }
        }
        
        @media (max-width: 768px) {
          body, html {
            max-width: 100vw;
            overflow-x: hidden;
          }
          
          section {
            padding: 30px 15px !important;
            overflow-x: hidden;
          }
          
          section:first-of-type {
            padding: 100px 20px 25px 20px !important;
          }
          
          section:first-of-type h1 {
            font-size: 1.75rem !important;
            margin-bottom: 12px !important;
          }
          
          section:nth-of-type(2) {
            padding: 20px 0 0 0 !important;
          }
          
          section:nth-of-type(3) {
            padding: 10px 15px 60px 15px !important;
          }
          
          /* Project tabs container */
          .project-tabs {
            margin-bottom: 0 !important;
            justify-content: flex-start !important;
            padding-left: 15px;
            padding-right: 15px;
          }
          
          /* Tab buttons */
          button[style*="whiteSpace: 'nowrap'"] {
            font-size: 0.875rem !important;
            padding: 12px 20px !important;
            min-width: auto !important;
          }
          
          /* Project info grid */
          div[style*="gridTemplateColumns: '1fr 1fr'"] {
            grid-template-columns: 1fr !important;
            gap: 20px !important;
          }
          
          /* Image gallery */
          div[style*="height: '500px'"] {
            height: 300px !important;
          }
          
          div[style*="height: '100px'"] {
            height: 80px !important;
          }
          
          /* Fix grid items */
          div[style*="display: 'grid'"] {
            width: 100%;
            max-width: 100%;
          }
        }
        
        @media (max-width: 480px) {
          section:first-of-type h1 {
            font-size: 1.5rem !important;
          }
          
          button[style*="whiteSpace: 'nowrap'"] {
            font-size: 0.8rem !important;
            padding: 10px 16px !important;
          }
          
          .container {
            padding: 0 15px !important;
          }
        }
      `}</style>
    </div>
  );
};

export default ServicePage;